import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {
  AppRoutes,
  CopyValueToClipboardDirective,
  formattedTimeDifferenceUtil,
  GameDetailsModel,
  HostedGamesRoutes,
  locationTypeMapper,
  LocationTypeModel,
  MODAL_TOKEN,
  ModalProvider,
  NavigateDirective,
  RedirectToUserProfileDirective,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { IonicModule } from '@ionic/angular';
import { OrAvatarComponent } from '../or-avatar/or-avatar.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ShareGameDirective } from '../share-game';
import { switchMap, take } from 'rxjs';
import { Router } from '@angular/router';
import { ChannelDetailsQueryParam } from '../../../../messages/src/lib/application/enums';
import {
  ChannelDetailsByReferenceQueryHandler,
  ChannelDetailsQueryResult,
  OpenChatCommandHandler
} from '../../../../messages/src/lib/application/handlers';
import { ChannelDetailsPayloadModel } from '../../../../messages/src/lib/application/interfaces';
import { AppGameLocationType, ChatChannelType, Visibility } from '@core-http';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrIconComponent } from '../or-icon/or-icon.component';

@Component({
  selector: ' lib-game-details',
  templateUrl: './game-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ShareGameDirective,
    IonicModule,
    OrAvatarComponent,
    CopyValueToClipboardDirective,
    NavigateDirective,
    NgClass,
    RedirectToUserProfileDirective,
    NgTemplateOutlet,
    OrIconComponent
  ],
  providers: [OpenChatCommandHandler, ChannelDetailsByReferenceQueryHandler]
})
export class GameDetailsComponent {
  @ViewChild('descriptionEl') private readonly descriptionEl?: ElementRef<HTMLDivElement>;

  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly openChatCommandHandler: OpenChatCommandHandler = inject(OpenChatCommandHandler);
  private readonly channelDetailsByReferenceQueryHandler: ChannelDetailsByReferenceQueryHandler = inject(
    ChannelDetailsByReferenceQueryHandler
  );

  readonly game: InputSignal<GameDetailsModel> = input.required<GameDetailsModel>();
  readonly showHostInfo: InputSignal<boolean> = input<boolean>(false);
  readonly showPhoneNumber: InputSignal<boolean> = input<boolean>(true);
  readonly showMessage: InputSignal<boolean> = input<boolean>(true);
  readonly showNavigateLink: InputSignal<boolean> = input<boolean>(false);
  readonly showGameTypeDetails: InputSignal<boolean> = input<boolean>(false);
  readonly adminAccess: InputSignal<boolean> = input<boolean>(false);
  readonly showGoToChat: InputSignal<boolean> = input<boolean>(false);

  readonly onSendMessage: OutputEmitterRef<{ userId: string; displayName: string | undefined }> = output<{
    userId: string;
    displayName: string | undefined;
  }>();

  private readonly userData: Signal<UserModel | undefined> = toSignal<UserModel>(this.userDataProvider.userData$);

  protected readonly locationTypeMapper: Record<AppGameLocationType, LocationTypeModel> = locationTypeMapper;

  readonly isDescriptionExpanded: WritableSignal<boolean> = signal<boolean>(false);
  readonly isDescriptionOverflowing: WritableSignal<boolean> = signal<boolean>(false);

  readonly isMyGame: Signal<boolean> = computed<boolean>(() => {
    return this.userData()?.userId === this.game()?.host?.hostId;
  });

  readonly gameType: Signal<string> = computed<string>(() => {
    return this.game().isRecurring ? 'Powtarzalna' : 'Pojedyncza';
  });

  readonly gameVisibilityLabel: Signal<string> = computed<string>(() => {
    return this.game().visibility === Visibility.PRIVATE ? 'Prywatna' : 'Widoczna';
  });

  readonly hostRating: Signal<string | null> = computed<string | null>(() => {
    if (!this.game().host) return null;
    return this.game().host?.rating.hostRating ?? null;
  });

  constructor() {
    this.registerDescriptionMeasurement();
  }

  toggleDescription(): void {
    this.isDescriptionExpanded.update((value: boolean) => !value);
  }

  sendMessage(userId: string | undefined, displayName: string | undefined, event: Event): void {
    event.stopImmediatePropagation();
    if (userId) {
      this.onSendMessage.emit({ userId, displayName });
    }
  }

  showAdminDetails(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigateByUrl(`${AppRoutes.HOST}/${HostedGamesRoutes.FUTURE}/${this.game().gameId}`);
  }

  calculatelastActive(date: string | undefined): string {
    const adjustedDate: Date | null = date ? new Date(date) : null;
    return formattedTimeDifferenceUtil(adjustedDate).toLowerCase();
  }

  goToChat(event: MouseEvent, gameId: string): void {
    event.stopImmediatePropagation();

    const payload: ChannelDetailsPayloadModel = {
      param: ChannelDetailsQueryParam.GAME,
      gameId: gameId
    };

    this.channelDetailsByReferenceQueryHandler
      .details(payload)
      .pipe(
        take(1),
        switchMap((details: ChannelDetailsQueryResult) =>
          this.openChatCommandHandler.open(details.channelExternalId, ChatChannelType.multi)
        )
      )
      .subscribe();
  }

  private registerDescriptionMeasurement(): void {
    effect(() => {
      this.game();
      this.isDescriptionExpanded();
      queueMicrotask(() => this.measureDescription());
    });
  }

  private measureDescription(): void {
    if (!this.descriptionEl || this.isDescriptionExpanded()) return;
    const el: HTMLDivElement = this.descriptionEl.nativeElement;
    this.isDescriptionOverflowing.set(el.scrollHeight > el.clientHeight);
  }
}
